import TimeInput24 from "@/components/base/TimeInput24";

export type TransferCategory = "none" | "airport" | "region" | "hotel";
export type CarCount = 1 | 2 | 3;
export type FlightInfo = { airline: string; number: string; time: string };

export const transferKeyOf = (cat: TransferCategory, cars: CarCount): string => {
  if (cat === "none") return "none";
  if (cat === "airport") return `transfer_${cars}`;
  if (cat === "region") return `transfer_region_${cars}`;
  return `transfer_hotel_${cars}`;
};

const CAR_OPTIONS: CarCount[] = [1, 2, 3];

type Props = {
  idPrefix: "arrival" | "departure";
  label: string;
  isDe: boolean;
  category: TransferCategory;
  cars: CarCount;
  onCategoryChange: (c: TransferCategory) => void;
  onCarsChange: (c: CarCount) => void;
  flight: FlightInfo;
  onFlightChange: (f: FlightInfo) => void;
  pickup: string;
  onPickupChange: (v: string) => void;
  pickupTime: string;
  onPickupTimeChange: (v: string) => void;
  priceOf: (key: string) => number;
  money: (n: number) => string;
  inputClass: string;
  labelClass: string;
  labelClassName?: string;
};

export default function TransferPicker({
  idPrefix,
  label,
  isDe,
  category,
  cars,
  onCategoryChange,
  onCarsChange,
  flight,
  onFlightChange,
  pickup,
  onPickupChange,
  pickupTime,
  onPickupTimeChange,
  priceOf,
  money,
  inputClass,
  labelClass,
  labelClassName,
}: Props) {
  const categoryOptions: { key: TransferCategory; icon: string; label: string }[] = [
    { key: "none", icon: "ri-close-circle-line", label: isDe ? "Kein Transfer" : "No transfer" },
    { key: "airport", icon: "ri-flight-land-line", label: isDe ? "Flughafentransfer" : "Airport transfer" },
    {
      key: "region",
      icon: "ri-map-pin-line",
      label: isDe ? "Nord-/Ost-/Westbali" : "North/East/West Bali",
    },
    { key: "hotel", icon: "ri-hotel-line", label: isDe ? "Hotel (Umkreis 50 km)" : "Hotel (within 50 km)" },
  ];

  const activeKey = transferKeyOf(category, cars);
  const activePrice = priceOf(activeKey);
  const isDeparture = idPrefix === "departure";

  return (
    <div>
      <label className={labelClassName ?? labelClass}>{label}</label>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {categoryOptions.map((opt) => {
          const active = category === opt.key;
          return (
            <button
              key={opt.key}
              type="button"
              onClick={() => onCategoryChange(opt.key)}
              className={`flex items-center justify-between gap-3 text-left rounded-md border p-4 transition-colors cursor-pointer ${
                active
                  ? "border-primary-500 bg-primary-50"
                  : "border-background-200 bg-background-50 hover:border-primary-300"
              }`}
            >
              <span className="flex items-center gap-2 min-w-0">
                <i className={`${opt.icon} text-lg shrink-0 ${active ? "text-primary-600" : "text-foreground-500"}`}></i>
                <span className="text-sm font-medium text-foreground-950 truncate">{opt.label}</span>
              </span>
              <i
                className={`text-lg shrink-0 ${
                  active ? "ri-radio-button-fill text-primary-600" : "ri-radio-button-line text-foreground-300"
                }`}
              ></i>
            </button>
          );
        })}
      </div>

      {category !== "none" && (
        <div className="mt-4 space-y-4 rounded-md border border-primary-200 bg-primary-50 p-4">
          <div>
            <span className="block text-xs font-medium text-foreground-700 mb-2">
              {isDe ? "Anzahl Autos" : "Number of cars"}
            </span>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {CAR_OPTIONS.map((c) => {
                const key = transferKeyOf(category, c);
                const active = cars === c;
                return (
                  <button
                    key={c}
                    type="button"
                    onClick={() => onCarsChange(c)}
                    className={`rounded-md border px-3 py-2.5 text-left transition-colors cursor-pointer ${
                      active
                        ? "border-primary-500 bg-background-50"
                        : "border-background-300 bg-background-50 hover:border-primary-300"
                    }`}
                  >
                    <span className="flex items-center justify-between gap-2">
                      <span className="text-sm font-medium text-foreground-950 whitespace-nowrap">
                        {c} {isDe ? (c === 1 ? "Auto" : "Autos") : c === 1 ? "car" : "cars"}
                      </span>
                      {active && <i className="ri-check-line text-primary-600"></i>}
                    </span>
                    <span className="mt-0.5 block text-xs text-foreground-600 whitespace-nowrap">
                      {isDe ? "max." : "max."} {c * 3} {isDe ? "Pers." : "ppl."} · {money(priceOf(key))}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {category === "airport" && (
            <div className="border-t border-primary-200 pt-4">
              <p className="flex items-center gap-2 text-sm font-medium text-foreground-900 mb-3">
                <i className="ri-flight-land-line text-accent-600"></i>
                {isDe ? "Flugdaten" : "Flight details"}
                <span className="text-xs font-normal text-foreground-500">
                  {isDe ? "(optional)" : "(optional)"}
                </span>
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label htmlFor={`${idPrefix}_flight_airline`} className={labelClass}>
                    {isDe ? "Fluggesellschaft" : "Airline"}
                  </label>
                  <input
                    id={`${idPrefix}_flight_airline`}
                    name={`${idPrefix}_flight_airline`}
                    type="text"
                    value={flight.airline}
                    onChange={(e) => onFlightChange({ ...flight, airline: e.target.value })}
                    placeholder={isDe ? "z. B. Singapore Airlines" : "e.g. Singapore Airlines"}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label htmlFor={`${idPrefix}_flight_number`} className={labelClass}>
                    {isDe ? "Flugnummer" : "Flight number"}
                  </label>
                  <input
                    id={`${idPrefix}_flight_number`}
                    name={`${idPrefix}_flight_number`}
                    type="text"
                    value={flight.number}
                    onChange={(e) => onFlightChange({ ...flight, number: e.target.value })}
                    placeholder={isDe ? "z. B. SQ 944" : "e.g. SQ 944"}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label htmlFor={`${idPrefix}_flight_time`} className={labelClass}>
                    {isDeparture
                      ? isDe
                        ? "Abflugszeit"
                        : "Departure time"
                      : isDe
                      ? "Ankunftszeit"
                      : "Arrival time"}
                  </label>
                  <TimeInput24
                    id={`${idPrefix}_flight_time`}
                    name={`${idPrefix}_flight_time`}
                    value={flight.time}
                    onChange={(v) => onFlightChange({ ...flight, time: v })}
                    isDe={isDe}
                    ariaLabel={
                      isDeparture
                        ? isDe
                          ? "Abflugszeit"
                          : "Departure time"
                        : isDe
                        ? "Ankunftszeit"
                        : "Arrival time"
                    }
                    className={inputClass}
                  />
                  <p className="mt-1 flex items-start gap-1.5 text-xs text-foreground-500">
                    <i className="ri-time-line mt-0.5"></i>
                    <span>{isDe ? "24-Stunden-Format, z. B. 18:20" : "24-hour format, e.g. 18:20"}</span>
                  </p>
                </div>
              </div>
            </div>
          )}

          {category === "region" && (
            <div className="border-t border-primary-200 pt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label htmlFor={`${idPrefix}_pickup`} className={labelClass}>
                  {isDe ? "Hotelname" : "Hotel name"}
                  <span className="text-xs font-normal text-foreground-500 ml-1">
                    {isDe ? "(Nord-/Ost-/Westbali)" : "(North/East/West Bali)"}
                  </span>
                </label>
                <input
                  id={`${idPrefix}_pickup`}
                  name={`${idPrefix}_pickup`}
                  type="text"
                  value={pickup}
                  onChange={(e) => onPickupChange(e.target.value)}
                  placeholder={isDe ? "z. B. Candidasa" : "e.g. Candidasa"}
                  className={inputClass}
                />
              </div>
              <div>
                <label htmlFor={`${idPrefix}_pickup_time`} className={labelClass}>
                  {isDeparture ? (isDe ? "Abreisezeit" : "Departure time") : isDe ? "Abholzeit" : "Pickup time"}
                </label>
                <TimeInput24
                  id={`${idPrefix}_pickup_time`}
                  name={`${idPrefix}_pickup_time`}
                  value={pickupTime}
                  onChange={onPickupTimeChange}
                  isDe={isDe}
                  ariaLabel={isDeparture ? (isDe ? "Abreisezeit" : "Departure time") : isDe ? "Abholzeit" : "Pickup time"}
                  className={inputClass}
                />
                <p className="mt-1 flex items-start gap-1.5 text-xs text-foreground-500">
                  <i className="ri-time-line mt-0.5"></i>
                  <span>
                    {isDeparture
                      ? isDe
                        ? "Gewünschte Abreisezeit, z. B. 14:30"
                        : "Desired departure time, e.g. 14:30"
                      : isDe
                      ? "Gewünschte Abholzeit, z. B. 14:30"
                      : "Desired pickup time, e.g. 14:30"}
                  </span>
                </p>
              </div>
            </div>
          )}

          {category === "hotel" && (
            <div className="border-t border-primary-200 pt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label htmlFor={`${idPrefix}_pickup`} className={labelClass}>
                  {isDe ? "Hotelname" : "Hotel name"}
                  <span className="text-xs font-normal text-foreground-500 ml-1">
                    {isDe ? "(Umkreis 50 km)" : "(within 50 km)"}
                  </span>
                </label>
                <input
                  id={`${idPrefix}_pickup`}
                  name={`${idPrefix}_pickup`}
                  type="text"
                  value={pickup}
                  onChange={(e) => onPickupChange(e.target.value)}
                  placeholder={isDe ? "z. B. Hotel in Canggu" : "e.g. a hotel in Canggu"}
                  className={inputClass}
                />
              </div>
              <div>
                <label htmlFor={`${idPrefix}_pickup_time`} className={labelClass}>
                  {isDeparture ? (isDe ? "Abreisezeit" : "Departure time") : isDe ? "Abholzeit" : "Pickup time"}
                </label>
                <TimeInput24
                  id={`${idPrefix}_pickup_time`}
                  name={`${idPrefix}_pickup_time`}
                  value={pickupTime}
                  onChange={onPickupTimeChange}
                  isDe={isDe}
                  ariaLabel={isDeparture ? (isDe ? "Abreisezeit" : "Departure time") : isDe ? "Abholzeit" : "Pickup time"}
                  className={inputClass}
                />
              </div>
            </div>
          )}

          <div className="flex items-center justify-between text-sm border-t border-primary-200 pt-3">
            <span className="text-foreground-700">{isDe ? "Transferpreis" : "Transfer price"}</span>
            <span className="font-semibold text-primary-700 whitespace-nowrap">{money(activePrice)}</span>
          </div>
        </div>
      )}
    </div>
  );
}